import { Card, Checkbox, Flex, Spin } from 'antd';
import { BaseButton } from 'components/button/button-base';
import { ErrorListDisplay } from 'components/ErrorDisplay';
import { Link, Text, Title } from 'components/mvp-typography';
import { PrintableRootPage } from 'components/page/page-2';
import {
  useGetPrincipalUser,
  useSubmitPrincipalUserMutation
} from 'lib/hooks/tanstack/use-getClinicInfo';
import { useQueryErrorNotifier } from 'lib/hooks/use-query-error-notifier';
import { useClinicianContext } from 'lib/hooks/use-resolveCurrentClinicInfo';
import { useErrorList } from 'lib/utils/useErrorList';
import { useState } from 'react';
import { ClinicRoutes } from 'routes';
import { BillingInfo } from './_components/billing-info';
import { PaymentDetails } from './_components/payment-details';
import { SettingsSection } from './_components/settings-section';

export function BillingSettingsPage() {
  const { currentClinic } = useClinicianContext();
  const principalUserQuery = useGetPrincipalUser(currentClinic);
  const submitPrincipalUserMutation = useSubmitPrincipalUserMutation();
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const errorList = useErrorList();

  useQueryErrorNotifier(
    submitPrincipalUserMutation,
    errorList,
    'PrincipalUser'
  );

  if (principalUserQuery.isLoading) {
    return (
      <PrintableRootPage title="Billing Settings">
        <Flex align="center" justify="center" style={{ minHeight: '200px' }}>
          <Spin size="large" />
        </Flex>
      </PrintableRootPage>
    );
  }

  const principalUserData = principalUserQuery.data?.data;

  // Show consent screen if there is no Principal User set, or if the status is "pending"
  const showConsentScreen =
    !principalUserData || principalUserData.status === 'pending';

  const handleConfirm = () => {
    if (!isAuthorized) return;
    errorList.reset();
    submitPrincipalUserMutation.mutate({
      clinicId: currentClinic,
      status: 'confirm'
    });
  };

  if (showConsentScreen) {
    return (
      <PrintableRootPage title="Billing Settings">
        <Flex vertical gap={24} style={{ maxWidth: 600, margin: '40px auto' }}>
          <Card>
            <Flex vertical gap={24} align="center">
              <Title
                level={4}
                style={{
                  textAlign: 'center',
                  fontWeight: 'normal',
                  lineHeight: '1.5'
                }}
              >
                Please confirm you are the authorized Principal User for this
                organisation.
              </Title>

              <Flex gap={12} align="center">
                <Checkbox
                  checked={isAuthorized}
                  onChange={e => setIsAuthorized(e.target.checked)}
                >
                  Yes, I am the authorized Principal User.
                </Checkbox>
              </Flex>

              <Text
                style={{
                  display: 'block',
                  textAlign: 'center',
                  color: '#8c8c8c'
                }}
              >
                If you are not the authorized Principal decision maker, please
                go to the{' '}
                <Link
                  href={ClinicRoutes.ClinicUserManagement}
                  style={{ fontWeight: 'bold' }}
                >
                  user management page
                </Link>{' '}
                to invite a Clinician and mark them as the principal.
              </Text>

              <BaseButton
                type="primary"
                size="large"
                block
                disabled={!isAuthorized}
                loading={submitPrincipalUserMutation.isPending}
                onClick={handleConfirm}
              >
                Confirm
              </BaseButton>

              <ErrorListDisplay errorList={errorList} closeable />
            </Flex>
          </Card>
        </Flex>
      </PrintableRootPage>
    );
  }

  return (
    <PrintableRootPage title="Billing Settings">
      <Flex gap={16} vertical>
        <Flex vertical gap={16 / 2}>
          {/* <Title level={2}>Info</Title> */}
          <Card>
            <Flex gap={16} vertical>
              <BillingInfo />
              <PaymentDetails />
              <Flex vertical gap={16 / 2}>
                <Title level={2}>Settings</Title>
                <SettingsSection />
              </Flex>
            </Flex>
          </Card>
        </Flex>
      </Flex>
    </PrintableRootPage>
  );
}
